import type { AxiosError } from "axios";
import type { UseQueryResult } from "react-query";
import { useQuery } from "react-query";

import apiBase from "../../utils/apiBase";
import type { InfoByModel } from "./useBilling";

export interface IUsageInfo {
  cogCachePrice: number;
  costSavings: number;
  totalActiveInputTokensUsed: number;
  totalActiveOutputTokensUsed: number;
  totalCachedInputTokensUsed: number;
  totalCachedOutputTokensUsed: number;
  perModel: {
    [key: string]: {
      cached: InfoByModel;
      active: InfoByModel;
      costSavings: number;
      cogCachePrice: number;
      azureOpenAiPrice: number;
    };
  };
}

export interface ITokenUsageTiers {
  tiers: {
    id: number;
    from: number;
    to: number;
    discounts: {
      key: string;
      discount: number;
    }[];
  }[];
  modelPrices: {
    inputTokensPrice: number;
    outputTokensPrice: number;
    perTokens: number;
    model: string;
    displayName: string;
  }[];
}

export interface IUsageAnalytics {
  tokens: {
    date: string;
    items: {
      cacheType: string;
      model: string;
      count: number;
    }[];
  }[];
  prices: {
    date: string;
    costs: number;
    savings: number;
  }[];
}

export interface IUsageResponse {
  currentTierId: number;
  provider: string;
  pastBillingPeriods: number;
  range: {
    fromDate: string;
    toDate: string;
  };
  usage: IUsageInfo;
  tokenUsageTiers: ITokenUsageTiers;
  analytics: IUsageAnalytics;
}

const fetchUsageInfo = async (
  organizationId: string,
  billingPeriodOffset = 0,
) => {
  return await apiBase.get(`/organizations/${organizationId}/usage`, {
    params: {
      billingPeriodOffset,
    },
  });
};

export const useUsage = (
  organizationId: string,
  billingPeriodOffset = 0,
): UseQueryResult<IUsageResponse, AxiosError> => {
  return useQuery(
    ["usage", organizationId, billingPeriodOffset],
    () => fetchUsageInfo(organizationId, billingPeriodOffset),
    {
      enabled: !!organizationId,
    },
  );
};
